import { useCallback, useMemo, useRef, useState } from "react";
import type { ChatResultData, ChatResultItem } from "shared/types";

export const CHAT_PAGE_SIZE = 150;

export type StoreAction = "init" | "prepend" | "append" | "window";

export function useChatMessageStore() {
  const [messageMap, setMessageMap] = useState<Map<string, ChatResultItem>>(
    () => new Map(),
  );
  const [loadedPages, setLoadedPages] = useState<Set<number>>(() => new Set());
  const [reachedOldest, setReachedOldest] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastAction, setLastAction] = useState<StoreAction>("init");

  // Refs that mirror state — readable inside async loops without stale closures
  const reachedOldestRef = useRef(false);
  const loadedPagesRef = useRef<Set<number>>(new Set());
  const messageMapRef = useRef<Map<string, ChatResultItem>>(new Map());

  const gapFillAbortRef = useRef<AbortController | null>(null);

  // Sorted messages derived from messageMap (newest first)
  const sortedMessages = useMemo(() => {
    const msgs = Array.from(messageMap.values());
    msgs.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    return msgs;
  }, [messageMap]);

  // ── Merge helpers ─────────────────────────────────────────────────

  const mergeMessages = useCallback((newMessages: ChatResultItem[]) => {
    setMessageMap((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const msg of newMessages) {
        if (!next.has(msg.chat_id)) {
          changed = true;
        }
        next.set(msg.chat_id, msg);
      }
      const result = changed || newMessages.length !== prev.size ? next : prev;
      messageMapRef.current = result;
      return result;
    });
  }, []);

  const markReachedOldest = useCallback(() => {
    reachedOldestRef.current = true;
    setReachedOldest(true);
  }, []);

  const addPageResult = useCallback(
    (
      pageNum: number,
      data: ChatResultData,
      pageSize: number = CHAT_PAGE_SIZE,
    ) => {
      setLastAction(pageNum === 1 ? "init" : "prepend");
      mergeMessages(data.messages);

      setLoadedPages((prev) => {
        const next = new Set(prev);
        next.add(pageNum);
        loadedPagesRef.current = next;
        return next;
      });

      if (data.messages.length < pageSize) {
        markReachedOldest();
      }
    },
    [mergeMessages, markReachedOldest],
  );

  const addWindowResult = useCallback(
    (data: ChatResultData) => {
      setLastAction("window");
      mergeMessages(data.messages);
    },
    [mergeMessages],
  );

  const addMqttMessage = useCallback((msg: ChatResultItem) => {
    setLastAction("append");
    setMessageMap((prev) => {
      const next = new Map(prev);
      next.set(msg.chat_id, msg);
      messageMapRef.current = next;
      return next;
    });
  }, []);

  const updateMessage = useCallback(
    (chatId: string, updater: (msg: ChatResultItem) => ChatResultItem) => {
      setMessageMap((prev) => {
        const existing = prev.get(chatId);
        if (!existing) return prev;
        const next = new Map(prev);
        next.set(chatId, updater(existing));
        messageMapRef.current = next;
        return next;
      });
    },
    [],
  );

  // ── Page tracking (ref-based for async safety) ─────────────────────

  const getNextOlderPage = useCallback((): number | null => {
    if (reachedOldestRef.current) return null;
    if (loadedPagesRef.current.size === 0) return 1;
    const maxPage = Math.max(...loadedPagesRef.current);
    return maxPage + 1;
  }, []);

  // ── Reset (filter change / unmount) ───────────────────────────────

  const reset = useCallback(() => {
    gapFillAbortRef.current?.abort();
    gapFillAbortRef.current = null;
    setMessageMap(new Map());
    setLoadedPages(new Set());
    setReachedOldest(false);
    setIsLoadingInitial(true);
    setIsLoadingMore(false);
    setLastAction("init");
    reachedOldestRef.current = false;
    loadedPagesRef.current = new Set();
    messageMapRef.current = new Map();
  }, []);

  return {
    messageMap,
    messageMapRef,
    sortedMessages,
    lastAction,
    loadedPages,
    loadedPagesRef,
    reachedOldest,
    reachedOldestRef,
    isLoadingInitial,
    isLoadingMore,
    gapFillAbortRef,
    setIsLoadingInitial,
    setIsLoadingMore,
    mergeMessages,
    addPageResult,
    addWindowResult,
    addMqttMessage,
    updateMessage,
    reset,
    getNextOlderPage,
  };
}
